import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import timetableService from '../../services/timetableService';
import curriculumService from '../../services/curriculumService';
import { Download, Eye, Note, X, Check, PushPin, MapPin } from '@phosphor-icons/react';
import './TeacherTimetableSection.css';

function TeacherTimetableSection({ taughtClasses }) {
  const [view, setView] = useState('all'); // all, byClass, byDay
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [timetable, setTimetable] = useState({});
  const [daySchedule, setDaySchedule] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lessonNotes, setLessonNotes] = useState({});
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentNotesPeriod, setCurrentNotesPeriod] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [nextClass, setNextClass] = useState(null);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  
  // Subject color mapping
  const subjectColors = {
    'Mathematics': '#FF6B6B',
    'English': '#4ECDC4',
    'Science': '#45B7D1',
    'History': '#96CEB4',
    'Geography': '#FFEAA7',
    'Physics': '#DDA0DD',
    'Chemistry': '#FF8C94',
    'Biology': '#A8D8EA',
    'PE': '#FFD3B6',
    'Art': '#FFAFD9',
    'Music': '#FF9E64',
    'ICT': '#78E08F'
  };

  // Get color for subject
  const getSubjectColor = (subjectName) => {
    return subjectColors[subjectName] || '#95E1D3';
  };

  // Load lesson notes from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('teacherLessonNotes');
    if (saved) {
      setLessonNotes(JSON.parse(saved));
    }
  }, []);

  // Save lesson notes to localStorage
  const saveLessonNotes = (notes) => {
    setLessonNotes(notes);
    localStorage.setItem('teacherLessonNotes', JSON.stringify(notes));
  };

  // Get next class
  const getNextClass = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const dayName = days[now.getDay() === 0 ? 4 : now.getDay() - 1]; // Convert to our day format

    const allClasses = [];
    Object.values(timetable).forEach(dayClasses => {
      allClasses.push(...dayClasses);
    });

    const upcoming = allClasses.find(cls => {
      if (!cls.start_time) return false;
      const [hour, minute] = cls.start_time.split(':').map(Number);
      return hour > currentHour || (hour === currentHour && minute > currentMinute);
    });

    setNextClass(upcoming || null);
  };

  // Load all classes timetable
  const loadMySchedule = React.useCallback(() => {
    setLoading(true);
    timetableService.getMySchedule()
      .then(data => {
        setTimetable(data.timetable || {});
        setPeriods([]);
        getNextClass();
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load timetable');
      })
      .finally(() => setLoading(false));
  }, []);

  // Load specific class timetable
  const loadClassTimetable = (classId) => {
    setLoading(true);
    timetableService.getTeacherClassSchedule(classId)
      .then(data => {
        setTimetable(data.timetable || {});
        setSelectedClass(classId);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load class timetable');
      })
      .finally(() => setLoading(false));
  };

  // Load day schedule
  const loadDaySchedule = (day) => {
    setLoading(true);
    timetableService.getTeacherDaySchedule(day)
      .then(data => {
        setDaySchedule(data.schedule || []);
        setSelectedDay(day);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load day schedule');
      })
      .finally(() => setLoading(false));
  };

  // Export schedule to PDF
  const exportToPDF = () => {
    try {
      const element = document.querySelector('.timetable-all') || document.querySelector('.class-timetable') || document.querySelector('.day-schedule');
      if (!element) {
        toast.error('No schedule to export');
        return;
      }

      const printWindow = window.open('', '', 'height=600,width=800');
      const htmlContent = `
        <html>
          <head>
            <title>Teacher Schedule</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #333; }
              .period-card { 
                border: 1px solid #ddd; 
                padding: 10px; 
                margin: 5px 0; 
                border-radius: 5px;
              }
              .day-header { background-color: #f0f0f0; padding: 10px; font-weight: bold; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #4CAF50; color: white; }
            </style>
          </head>
          <body>
            <h1>Teacher Schedule - ${new Date().toLocaleDateString()}</h1>
            ${element.innerHTML}
          </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.print();
    } catch (err) {
      console.error(err);
      toast.error('Failed to export schedule');
    }
  };

  // Open notes modal
  const openNotesModal = async (period) => {
    const periodKey = `${period.class_id || period.class_name}_${period.period_id}`;
    setCurrentNotesPeriod({ ...period, key: periodKey });
    setNotesText(lessonNotes[periodKey] || '');
    if (period.class_id && period.subject_id) {
      try {
        const notes = await curriculumService.lessonNotes({ class_id: period.class_id, subject_id: period.subject_id, lesson_date: new Date().toISOString().slice(0, 10) });
        const serverNote = notes?.[0]?.content;
        if (serverNote) {
          setNotesText(serverNote);
          setLessonNotes(prev => ({ ...prev, [periodKey]: serverNote }));
        }
      } catch (error) {
        console.debug('Server lesson notes unavailable; using local notes', error);
      }
    }
    setShowNotesModal(true);
  };

  // Save notes
  const saveNotes = async () => {
    if (!currentNotesPeriod) return;
    
    const updated = {
      ...lessonNotes,
      [currentNotesPeriod.key]: notesText
    };
    saveLessonNotes(updated);
    if (currentNotesPeriod.class_id && currentNotesPeriod.subject_id) {
      try {
        const notes = await curriculumService.lessonNotes({ class_id: currentNotesPeriod.class_id, subject_id: currentNotesPeriod.subject_id, lesson_date: new Date().toISOString().slice(0, 10) });
        if (notes?.[0]?.id) {
          await curriculumService.updateLessonNote(notes[0].id, { content: notesText });
        } else {
          await curriculumService.createLessonNote({ class_id: currentNotesPeriod.class_id, subject_id: currentNotesPeriod.subject_id, lesson_date: new Date().toISOString().slice(0, 10), content: notesText });
        }
      } catch (error) {
        toast.error('Saved locally, but server lesson note could not be updated');
      }
    }
    setShowNotesModal(false);
    toast.success('Lesson notes saved');
  };

  // Delete notes
  const deleteNotes = () => {
    if (!currentNotesPeriod) return;
    
    const updated = { ...lessonNotes };
    delete updated[currentNotesPeriod.key];
    saveLessonNotes(updated);
    setShowNotesModal(false);
    toast.success('Lesson notes deleted');
  };

  // Load periods
  useEffect(() => {
    timetableService.listTeacherPeriods()
      .then(data => setPeriods(data.items || []))
      .catch(err => console.error(err));
  }, []);

  // Initial load
  useEffect(() => {
    if (view === 'all') {
      loadMySchedule();
    }
  }, [view, loadMySchedule]);

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    // If already in HH:MM or HH:MM:SS format, return formatted version
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
    
    // Try to parse as ISO datetime string
    try {
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      }
    } catch {
      // Fall through to return original
    }
    
    return timeString;
  };

  return (
    <div className="teacher-timetable-section">
      <div className="section-header">
        <div>
          <h2>Schedule & Timetable</h2>
          {nextClass && (
            <p className="next-class-info" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PushPin size={16} /> Next Class: <strong>{nextClass.class_name}</strong> ({nextClass.subject_name}) at {formatTime(nextClass.start_time)}
            </p>
          )}
        </div>
        <button 
          className="btn btn-secondary"
          onClick={exportToPDF}
          title="Export schedule as PDF"
        >
          <Download size={18} />
          Export PDF
        </button>
      </div>

      {/* View Selector */}
      <div className="view-selector">
        <button
          className={`view-btn ${view === 'all' ? 'active' : ''}`}
          onClick={() => setView('all')}
        >
          All Classes
        </button>
        <button
          className={`view-btn ${view === 'byClass' ? 'active' : ''}`}
          onClick={() => setView('byClass')}
        >
          By Class
        </button>
        <button
          className={`view-btn ${view === 'byDay' ? 'active' : ''}`}
          onClick={() => setView('byDay')}
        >
          By Day
        </button>
      </div>

      {/* View: All Classes */}
      {view === 'all' && (
        <div className="timetable-all">
          {loading ? (
            <div className="loading">Loading schedule...</div>
          ) : Object.keys(timetable).length > 0 ? (
            <div className="days-grid">
              {days.map(day => (
                <div key={day} className="day-column">
                  <h3 className="day-header">{day}</h3>
                  <div className="periods-list">
                    {(timetable[day] || []).map((period, idx) => {
                      const periodKey = `${period.class_id || period.class_name}_${period.period_id}`;
                      const hasNotes = lessonNotes[periodKey];
                      return (
                        <div 
                          key={idx} 
                          className="period-card"
                          style={{ borderLeftColor: getSubjectColor(period.subject_name), borderLeftWidth: '4px' }}
                        >
                          <span className="period-name">{period.period_name}</span>
                          <span className="class-info">
                            {period.class_name}
                            <br />
                            <span className="subject-badge" style={{ backgroundColor: getSubjectColor(period.subject_name) }}>
                              {period.subject_name}
                            </span>
                          </span>
                          <span className="time">
                            {period.start_time && period.end_time && (
                              <>
                                {formatTime(period.start_time)} - {formatTime(period.end_time)}
                              </>
                            )}
                          </span>
                          <button
                            className="notes-btn"
                            onClick={() => openNotesModal(period)}
                            title={hasNotes ? 'Edit notes' : 'Add notes'}
                            style={{ color: hasNotes ? '#4CAF50' : '#999' }}
                          >
                            <Note size={16} />
                            {hasNotes ? 'Edit' : 'Add'} Notes
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No classes assigned</p>
            </div>
          )}
        </div>
      )}

      {/* View: By Class */}
      {view === 'byClass' && (
        <div className="timetable-by-class">
          <div className="class-selector">
            <h3>Select a Class</h3>
            <select
              value={selectedClass || ''}
              onChange={(e) => {
                const classId = e.target.value;
                loadClassTimetable(classId);
              }}
              className="select-input"
            >
              <option value="">Choose a class</option>
              {taughtClasses.map(cls => (
                <option key={cls.class_id} value={cls.class_id}>
                  {cls.class_name}
                </option>
              ))}
            </select>
          </div>

          {selectedClass && (
            <div className="class-timetable">
              {loading ? (
                <div className="loading">Loading class schedule...</div>
              ) : Object.keys(timetable).length > 0 ? (
                <div className="days-grid">
                  {days.map(day => (
                    <div key={day} className="day-column">
                      <h3 className="day-header">{day}</h3>
                      <div className="periods-list">
                        {(timetable[day] || []).map((period, idx) => {
                          const periodKey = `${period.class_id || period.class_name}_${period.period_id}`;
                          const hasNotes = lessonNotes[periodKey];
                          return (
                            <div 
                              key={idx} 
                              className="period-card"
                              style={{ borderLeftColor: getSubjectColor(period.subject_name), borderLeftWidth: '4px' }}
                            >
                              <span className="period-name">{period.period_name}</span>
                              <span className="subject-info">
                                <span className="subject-badge" style={{ backgroundColor: getSubjectColor(period.subject_name) }}>
                                  {period.subject_name}
                                </span>
                                {period.room && (
                                  <>
                                    <br />
                                    <span className="room" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MapPin size={14} /> Room: {period.room}</span>
                                  </>
                                )}
                              </span>
                              <span className="time">
                                {period.start_time && period.end_time && (
                                  <>
                                    {formatTime(period.start_time)} - {formatTime(period.end_time)}
                                  </>
                                )}
                              </span>
                              <button
                                className="notes-btn"
                                onClick={() => openNotesModal(period)}
                                title={hasNotes ? 'Edit notes' : 'Add notes'}
                                style={{ color: hasNotes ? '#4CAF50' : '#999' }}
                              >
                                <Note size={16} />
                                {hasNotes ? 'Edit' : 'Add'} Notes
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No schedule for this class</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* View: By Day */}
      {view === 'byDay' && (
        <div className="timetable-by-day">
          <div className="day-selector">
            <h3>Select a Day</h3>
            <div className="day-buttons">
              {days.map(day => (
                <button
                  key={day}
                  className={`day-btn ${selectedDay === day ? 'active' : ''}`}
                  onClick={() => loadDaySchedule(day)}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div className="day-schedule">
            {loading ? (
              <div className="loading">Loading day schedule...</div>
            ) : daySchedule.length > 0 ? (
              <div className="schedule-list">
                {daySchedule.map((period, idx) => {
                  const periodKey = `${period.class_id || period.class_name}_${period.period_id}`;
                  const hasNotes = lessonNotes[periodKey];
                  return (
                    <div key={idx} className="schedule-item">
                      <div 
                        className="time-slot"
                        style={{ borderLeftColor: getSubjectColor(period.subject_name), borderLeftWidth: '4px' }}
                      >
                        <span className="time-range">
                          {period.start_time && period.end_time && (
                            <>
                              {formatTime(period.start_time)}<br />
                              {formatTime(period.end_time)}
                            </>
                          )}
                        </span>
                        <span className="period-label">{period.period_name}</span>
                      </div>
                      <div className="class-details">
                        <h4>{period.class_name}</h4>
                        <span className="subject-badge" style={{ backgroundColor: getSubjectColor(period.subject_name) }}>
                          {period.subject_name}
                        </span>
                        {period.room && <p className="room-info" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={14} /> Room: {period.room}</p>}
                        {hasNotes && (
                          <div className="notes-preview">
                            <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Note size={14} /> Notes:</strong> {lessonNotes[periodKey].substring(0, 50)}...
                          </div>
                        )}
                      </div>
                      <button
                        className="notes-btn"
                        onClick={() => openNotesModal(period)}
                        title={hasNotes ? 'Edit notes' : 'Add notes'}
                        style={{ color: hasNotes ? '#4CAF50' : '#999' }}
                      >
                        <Note size={18} />
                        {hasNotes ? 'Edit' : 'Add'} Notes
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <p>No classes scheduled for this day</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lesson Notes Modal */}
      {showNotesModal && (
        <div className="modal-overlay" onClick={() => setShowNotesModal(false)}>
          <div className="modal-content notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Note size={20} />
                Lesson Notes
              </h3>
              <button 
                className="close-btn"
                onClick={() => setShowNotesModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="period-info">
                <p><strong>Class:</strong> {currentNotesPeriod?.class_name}</p>
                <p><strong>Subject:</strong> {currentNotesPeriod?.subject_name}</p>
                <p><strong>Time:</strong> {formatTime(currentNotesPeriod?.start_time)} - {formatTime(currentNotesPeriod?.end_time)}</p>
              </div>

              <textarea
                className="notes-textarea"
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Add your lesson notes, topics, homework, or any important information..."
                rows="8"
              />
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-danger"
                onClick={deleteNotes}
                disabled={!lessonNotes[currentNotesPeriod?.key]}
              >
                Delete
              </button>
              <div className="modal-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowNotesModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={saveNotes}
                >
                  <Check size={16} />
                  Save Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherTimetableSection;
